'use client';

import { ProductCard } from '@components/Product/ProductCard';
import { ProductCardV2 } from '@components/Product/ProductCard.v2';
import { ProductRow as ProductRowUI } from '@components/Product/ProductRow';
import { ProductRowHeading } from '@components/Product/ProductRowHeading';
import { getProductCardTabletClasses } from '@components/Product/productCardTabletStyles';
import { TrackPromotionImpression } from '@components/TrackPromotionImpression/TrackPromotionImpression';
import { usePromoTrackingEvents } from '@components/TrackPromotionImpression/usePromoTrackingEvents';
import { useEntitlements } from '@contexts/EntitlementsContext';
import { useProductTags } from '@contexts/ProductTagsContext';
import { useRouter } from '@contexts/Router/RouterContext';
import { ProductRowFragment } from '@graphql/generated-contentful/graphql';
import { useFlags } from '@lib/flags/amplitude/client';
import { FLAG_TREATMENT, Flags } from '@lib/flags/flagConstants';
import { longTextToProductIds } from '@lib/util/longTextToProductIds';
import c from 'classnames';
import {
  mapToProductCardProps,
  mapToProductCardV2Props,
} from './mapToProductCardProps';
import { usePackagesByIds } from './usePackagesByIds';

export type ProductRowProps = {
  data: ProductRowFragment;
  position: number;
};

const ProductRow = ({ data, position }: ProductRowProps) => {
  const { locale } = useRouter();
  const { flags } = useFlags();
  const {
    productRowTitle,
    productRowDescription,
    text,
    linkHref,
    linkTitle,
    productIdList,
    titleInternal,
    trackAsPromo,
    productColumnCount,
  } = data;

  const isProductCardV2Enabled =
    flags[Flags.UI_AND_MISSING_FILTERS]?.value === FLAG_TREATMENT;

  const DEFAULT_NUMBER_OF_COLUMNS = 4;

  const link =
    linkHref && linkTitle ? { href: linkHref, label: linkTitle } : undefined;
  const productIDs = productIdList ? longTextToProductIds(productIdList) : [];
  const { data: products, error, isLoading } = usePackagesByIds(productIDs);
  const productTags = useProductTags();
  const { getUserEntitlement } = useEntitlements();

  const { promoClickTrackingEvent, promoViewTrackingEvent } =
    usePromoTrackingEvents({
      title: titleInternal || '',
      typename: 'ProductRow',
      position,
      trackAsPromo: trackAsPromo ?? false,
      ctas: link?.label ? [link.label] : [],
    });

  if (isLoading || error || !products || products.length === 0) {
    return null;
  }

  const productCards = isProductCardV2Enabled
    ? mapToProductCardV2Props(products, getUserEntitlement)
    : mapToProductCardProps(products, productTags, getUserEntitlement);

  return (
    <TrackPromotionImpression
      onImpression={promoViewTrackingEvent}
      shouldTrack={trackAsPromo ?? false}
    >
      <ProductRowHeading
        title={productRowTitle}
        description={productRowDescription}
        text={text}
        link={link}
        onLinkClick={promoClickTrackingEvent}
      />
      <ProductRowUI gridCols={productColumnCount ?? DEFAULT_NUMBER_OF_COLUMNS}>
        {productCards.map((product, index) =>
          isProductCardV2Enabled ? (
            <ProductCardV2
              key={product.productId}
              className={c(getProductCardTabletClasses(index, products.length))}
              locale={locale}
              {...product}
            />
          ) : (
            <ProductCard
              key={product.productId}
              className={getProductCardTabletClasses(index, products.length)}
              locale={locale}
              {...product}
            />
          )
        )}
      </ProductRowUI>
    </TrackPromotionImpression>
  );
};

export default ProductRow;
